import { Button } from '@nextui-org/react'
import { useSnackbar } from 'notistack'
import { FC, useCallback, useState } from 'react'
import {
  MdArrowOutward, MdCheck, MdInventory2, MdLocalShipping, MdOutlineQrCode2,
  MdPlace, MdWarningAmber, MdWhereToVote
} from 'react-icons/md'
import { useSelector } from 'react-redux'
import ApiStateView from '../ApiState'
import BackButton from '../BackButton'
import Panel from '../Panel'
import { trackingApi, labelsApi, resolveLabelUrl, TrackingDetail, EstadoLocal } from '../../api'
import { TERMINO_ETIQUETA } from '../../constants'
import { useApi } from '../../hooks/useApi'
import { useSelectedSeller } from '../../hooks/useSelectedSeller'
import { useLogisticBox } from '../../context/LogisticBoxContext'
import { IAppStore } from '../../../../../models/store.model'

// Paleta de marca Reuse
const FOUNDATION = '#151930'
const SOLID = '#37417f'
const RE_BLUE = '#4b75f7'
const LILAC = '#afb9ff'
const ALERT = '#f06464'

// Posición en el stepper de 4 hitos · Creada → Retiro → En camino → Entregada.
// 'atrasada' se retiró: se ubica en "En camino".
const STEP_INDEX: Record<EstadoLocal, number> = { lista: 0, recogida: 1, en_transito: 2, entregada: 3, atrasada: 2 }

const STEPS = [
  { key: 'creada', label: 'Creada', Icon: MdOutlineQrCode2 },
  { key: 'retiro', label: 'Retiro', Icon: MdInventory2 },
  { key: 'en_camino', label: 'En camino', Icon: MdLocalShipping },
  { key: 'entregada', label: 'Entregada', Icon: MdWhereToVote }
]

const fmtFechaHora = (iso: string | null): { fecha: string, hora: string } => {
  if (iso === null || iso === '') return { fecha: '—', hora: '' }
  const d = new Date(iso)
  const dias = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']
  const meses = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
  const fecha = `${dias[d.getDay()]} ${d.getDate()} ${meses[d.getMonth()]}`
  const hora = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  return { fecha, hora }
}

/* ----------------------------- Stepper de 4 hitos ----------------------------- */
const ProgressStepper: FC<{ estado: EstadoLocal }> = ({ estado }) => {
  const current = STEP_INDEX[estado]
  const isAtrasada = estado === 'atrasada'
  const isDone = estado === 'entregada'

  return (
    <div className='flex items-start w-full'>
      {STEPS.map((step, i) => {
        const done = i < current || isDone
        const active = i === current && !isDone
        const bg = active
          ? (isAtrasada ? ALERT : FOUNDATION)
          : done ? SOLID : '#eceef6'
        const iconColor = active || done ? '#fff' : '#9aa1c0'
        const Icon = active && isAtrasada ? MdWarningAmber : step.Icon
        return (
          <div key={step.key} className='flex items-start flex-1 last:flex-none'>
            <div className='flex flex-col items-center'>
              <span className={`mb-2 text-[14px] text-center leading-tight ${active || done ? 'font-semibold text-[#151930]' : 'text-[#151930]/40'}`}>
                {step.label}
              </span>
              <div
                className='w-12 h-12 rounded-full flex items-center justify-center transition-colors'
                style={{ background: bg, boxShadow: active ? `0 0 0 5px ${(isAtrasada ? ALERT : RE_BLUE)}26` : undefined }}
              >
                {done ? <MdCheck className='text-xl' style={{ color: '#fff' }} /> : <Icon className='text-xl' style={{ color: iconColor }} />}
              </div>
            </div>
            {i < STEPS.length - 1 && (
              <div className='flex-1 h-[3px] mx-1 mt-[34px] rounded-full' style={{ background: i < current || isDone ? SOLID : '#e3e6f0' }} />
            )}
          </div>
        )
      })}
    </div>
  )
}

const RecoleccionTrackingView: FC = () => {
  const userId = useSelector((s: IAppStore) => s.user?.id ?? null)
  const idSeller = useSelectedSeller()
  const { selectedTracking } = useLogisticBox()
  const { enqueueSnackbar } = useSnackbar()
  const [syncing, setSyncing] = useState(false)

  const fetchDetail = useCallback(async (): Promise<TrackingDetail | null> => {
    if (selectedTracking === null) throw new Error('Selecciona una recolección desde Recolecciones')
    return await trackingApi.detail(selectedTracking, idSeller)
  }, [selectedTracking, idSeller])

  const detail = useApi<TrackingDetail | null>(fetchDetail, [selectedTracking, idSeller])

  const handleInterno = async (tipo: 'salida' | 'llegada'): Promise<void> => {
    const idCollection = detail.data?.id_collection ?? null
    if (idCollection === null) return
    setSyncing(true)
    try {
      const r = await labelsApi.markInternalCollection(idCollection, tipo, idSeller, userId === 0 ? null : userId)
      enqueueSnackbar(r.message, { variant: 'success' })
      await detail.reload()
    } catch (e: any) {
      enqueueSnackbar(e?.message ?? 'No se pudo registrar', { variant: 'error' })
    } finally { setSyncing(false) }
  }

  const d = detail.data
  const estado = d?.estado_local ?? 'lista'
  const esInterno = d?.estado_dhl?.startsWith('interno') ?? false
  const pdfUrl = resolveLabelUrl(d?.pdf_url ?? null, idSeller)
  const eventos = d?.eventos ?? []
  const sync = fmtFechaHora(d?.ultimo_sync_dhl_at ?? null)

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between flex-wrap gap-3'>
        <BackButton to='etiquetas' label='Volver a Recolecciones' />
      </div>

      <ApiStateView
        loading={detail.loading}
        error={detail.error}
        onRetry={detail.reload}
        empty={!detail.loading && d === null}
        emptyText='No se encontró tracking para esta recolección'
      >
        {d !== null && (
          <div className='space-y-4'>
            {/* ───── Card principal estilo tracking público ───── */}
            <Panel className='!p-0 overflow-hidden'>
              {/* Hero oscuro */}
              <div
                className='relative px-6 py-6 overflow-hidden'
                style={{ background: `linear-gradient(120deg, ${FOUNDATION} 0%, ${SOLID} 100%)` }}
              >
                {/* Decoración circular (guiño al isotipo) */}
                <div className='absolute -right-10 -top-16 w-48 h-48 rounded-full' style={{ border: `2px solid ${LILAC}33` }} />
                <div className='absolute -right-2 -bottom-20 w-40 h-40 rounded-full' style={{ border: `2px solid ${LILAC}22` }} />

                <div className='relative flex items-start justify-between flex-wrap gap-3'>
                  <div>
                    <div className=' text-2xl font-bold text-white tracking-wide'>
                      {d.tracking_number}
                    </div>
                    <div className='text-xs text-white/55 mt-1'>
                      Recolección N° <span className='font-semibold text-white/80'>{d.id_collection}</span>
                      <span className='mx-1.5'>·</span>{d.store}
                    </div>
                  </div>
                  <div className='flex flex-col items-end gap-2'>
                    <span
                      className='text-[14px] font-bold uppercase tracking-wider text-white px-3 py-1.5 rounded-full'
                      style={{ background: RE_BLUE }}
                    >
                      {d.courier}
                    </span>
                    {esInterno && (
                      <span className='text-[10px] font-semibold uppercase tracking-wider text-[#151930] px-2.5 py-1 rounded-full' style={{ background: LILAC }}>
                        Retiro Reuse
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Cuerpo claro */}
              <div className='p-6 space-y-6'>
                {/* Origen / Destino */}
                <div className='rounded-2xl bg-[#f3f4f8] p-4'>
                  <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                    <div>
                      <div className='text-xs text-[#151930]/55'>Origen</div>
                      <div className='text-sm font-semibold text-[#151930] mt-0.5'>{d.origen || d.store}</div>
                    </div>
                    <div className='sm:border-l sm:border-[#dfe2ee] sm:pl-4'>
                      <div className='text-xs text-[#151930]/55'>Destino</div>
                      <div className='text-sm font-semibold text-[#151930] mt-0.5'>{d.destino}</div>
                    </div>
                  </div>
                </div>

                {/* Stepper */}
                <ProgressStepper estado={estado} />

                {/* Meta inferior */}
                <div className='flex items-center justify-between flex-wrap gap-3 pt-1 border-t border-[#eef0f7] text-xs text-[#151930]/55'>
                  <div className='flex items-center gap-4 flex-wrap'>
                    <span><span className='text-[#151930]/40'>Equipos:</span> <span className='font-semibold text-[#151930]'>{d.cantidad_equipos_real ?? d.cantidad_equipos_estimada ?? '—'}</span></span>
                    <span><span className='text-[#151930]/40'>ETA:</span> <span className='font-semibold text-[#151930]'>{d.eta_entrega !== null ? fmtFechaHora(d.eta_entrega).fecha : '—'}</span></span>
                    <span><span className='text-[#151930]/40'>Ref:</span> <span className=' font-medium text-[#151930]'>{d.ourReference}</span></span>
                  </div>
                  <span className='text-[#151930]/40'>Últ. sync {sync.fecha}{sync.hora ? ` · ${sync.hora}` : ''}</span>
                </div>
              </div>
            </Panel>

            {/* ───── Timeline + acciones ───── */}
            <div className='grid grid-cols-1 lg:grid-cols-3 gap-4'>
              {/* Historial */}
              <div className='lg:col-span-2'>
                <Panel>
                  <div className='flex items-center justify-between mb-4'>
                    <h3 className='font-semibold text-[#151930] text-sm'>Historial de eventos</h3>
                    <span className='text-[14px] text-[#151930]/45'>{eventos.length} eventos · {d.courier}</span>
                  </div>

                  {eventos.length === 0
                    ? (
                      <div className='flex flex-col items-center justify-center py-10 text-center'>
                        <div className='w-12 h-12 rounded-full bg-[#f3f4fb] flex items-center justify-center mb-3'>
                          <MdLocalShipping className='text-2xl text-[#151930]/30' />
                        </div>
                        <p className='text-sm text-[#151930]/60'>Aún no hay eventos sincronizados</p>
                      </div>
                      )
                    : (
                      <ol className='relative'>
                        {eventos.map((ev, i) => {
                          const isLast = i === eventos.length - 1
                          const { fecha, hora } = fmtFechaHora(ev.fecha_evento)
                          return (
                            <li key={ev.id ?? i} className='relative pl-7 pb-5 last:pb-0'>
                              <span className='absolute left-0 top-0.5 w-4 h-4 rounded-full flex items-center justify-center' style={{ background: isLast ? `${RE_BLUE}22` : '#eef0f8' }}>
                                <span className='w-2 h-2 rounded-full' style={{ background: isLast ? RE_BLUE : SOLID }} />
                              </span>
                              {!isLast && <span className='absolute left-[7px] top-5 bottom-0 w-[2px] bg-[#eef0f8]' />}
                              <div className='flex items-start justify-between gap-3'>
                                <div>
                                  <div className='text-sm font-medium text-[#151930]'>{ev.evento_codigo ?? ev.descripcion ?? 'Evento'}</div>
                                  {ev.descripcion !== null && ev.evento_codigo !== null && (
                                    <div className='text-xs text-[#151930]/55 mt-0.5'>{ev.descripcion}</div>
                                  )}
                                  {ev.ubicacion !== null && (
                                    <div className='flex items-center gap-1 text-[14px] text-[#151930]/45 mt-0.5'>
                                      <MdPlace className='text-xs' />{ev.ubicacion}
                                    </div>
                                  )}
                                </div>
                                <div className='text-right shrink-0'>
                                  <div className='text-xs font-medium text-[#151930]'>{fecha}</div>
                                  <div className='text-[14px] text-[#151930]/45'>{hora}</div>
                                </div>
                              </div>
                            </li>
                          )
                        })}
                      </ol>
                      )}
                </Panel>
              </div>

              {/* Acciones */}
              <div className='space-y-4'>
                {pdfUrl !== null && (
                  <Panel>
                    <h3 className='font-semibold text-[#151930] text-sm mb-3'>{TERMINO_ETIQUETA}</h3>
                    <a
                      href={pdfUrl} target='_blank' rel='noreferrer'
                      className='flex items-center justify-between rounded-xl border border-[#eef0f7] hover:border-[#c9d0ea] px-3 py-3 transition-colors group'
                    >
                      <div className='flex items-center gap-2'>
                        <div className='w-9 h-9 rounded-lg bg-[#f3f4fb] flex items-center justify-center'>
                          <MdOutlineQrCode2 className='text-lg' style={{ color: SOLID }} />
                        </div>
                        <div>
                          <div className='text-sm font-medium text-[#151930]'>{esInterno ? `${TERMINO_ETIQUETA} Reuse` : `${TERMINO_ETIQUETA} ${d.courier}`}</div>
                          <div className='text-[14px] text-[#151930]/50'>Abrir pdf</div>
                        </div>
                      </div>
                      <MdArrowOutward className='text-[#151930]/40 group-hover:text-[#37417f]' />
                    </a>
                  </Panel>
                )}

                {esInterno && (
                  <Panel>
                    <h3 className='font-semibold text-[#151930] text-sm mb-1'>Retiro Reuse</h3>
                    <p className='text-[14px] text-[#151930]/50 mb-3'>Avanza el estado manualmente a medida que se mueve la caja.</p>
                    <div className='space-y-2'>
                      <Button
                        fullWidth size='sm'
                        className='bg-black text-white text-xs h-9'
                        startContent={<MdLocalShipping className='text-base' />}
                        isDisabled={syncing || estado !== 'lista'}
                        onPress={() => { void handleInterno('salida') }}
                      >
                        Marcar salida de tienda
                      </Button>
                      <Button
                        fullWidth size='sm' variant='flat'
                        className='text-xs h-9'
                        startContent={<MdInventory2 className='text-base' />}
                        isDisabled={syncing || estado === 'lista' || estado === 'entregada'}
                        onPress={() => { void handleInterno('llegada') }}
                      >
                        Marcar llegada a bodega
                      </Button>
                    </div>
                  </Panel>
                )}
              </div>
            </div>
          </div>
        )}
      </ApiStateView>
    </div>
  )
}

export default RecoleccionTrackingView
